import React, { useCallback } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Paper,
  IconButton,
  alpha,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

/**
 * Enhanced DynamicField Component with File Upload Support
 * 
 * @param {Object} props
 * @param {string} props.type - Field type: 'text', 'email', 'tel', 'date', 'number', 'select', 'textarea', 'file', 'checkbox', 'radio'
 * @param {string} props.label - Field label
 * @param {string} props.name - Field name/key
 * @param {any} props.value - Current field value
 * @param {function} props.onChange - Change handler that receives (name, value)
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.placeholder - Placeholder text
 * @param {Array} props.options - Options for select/radio field
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {number} props.rows - Number of rows for textarea
 * @param {number} props.minRows - Minimum rows for multiline
 * @param {boolean} props.multiline - Whether field is multiline
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {string} props.helperText - Helper text below field
 * @param {boolean} props.error - Whether field has error
 * @param {string} props.errorText - Error message text
 * @param {function} props.onValidate - Custom validation function
 * @param {Object} props.inputProps - Additional props for the input element (e.g., maxLength, pattern)
 * @param {Array} props.files - Array of uploaded files (for file type)
 * @param {function} props.onFileRemove - File remove handler (index)
 * @param {function} props.onFileChoose - File choose handler
 * @param {string} props.accept - Accepted file types
 * @param {string} props.maxSize - Max file size text
 * @param {string} props.supportedFormats - Supported file formats text
 */
export const DynamicFieldAdvanced = ({
  type = "text",
  label,
  name,
  value,
  onChange,
  onInputChange,
  required = false,
  placeholder = "Enter here",
  options = [],
  disabled = false,
  rows,
  minRows,
  multiline = false,
  sx = {},
  helperText,
  error = false,
  errorText,
  onValidate,
  inputProps,
  numericOnly = false,
  // File upload specific
  files = [],
  onFileRemove,
  onFileChoose,
  accept = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg",
  maxSize = "4MB",
  supportedFormats = "jpeg, pdf, png",
}) => {
  const handleChange = useCallback((event) => {
    let newValue = type === "checkbox" ? event.target.checked : event.target.value;

    // Numeric only restriction
    if (numericOnly || type === "tel" || type === "phone") {
      if (typeof newValue === "string") {
        newValue = newValue.replace(/\D/g, "");
      }
    }

    // Custom validation
    if (onValidate) {
      const validationResult = onValidate(newValue);
      if (validationResult.error) {
        // Handle validation error
        onChange(name, newValue, validationResult);
        return;
      }
    }

    // Immediate onChange
    onChange(name, newValue);
  }, [type, onChange, onValidate]);


  const FileList = React.memo(({ files, onRemove }) => {
    const handleRemove = useCallback((idx) => {
      onRemove(idx);
    }, [onRemove]);

    if (!files || files.length === 0) return null;
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
        {files.map((f, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: 'rgba(16, 185, 129, 0.06)',
              border: '1px solid rgba(16, 185, 129, 0.15)',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.02)',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'rgba(16, 185, 129, 0.1)',
              }
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: '#059669' }} />
            {f.url ? (
              <Typography
                variant="caption"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await fetch(`/api/onboarding/proxy-download?url=${encodeURIComponent(f.url)}`);
                    if (!response.ok) throw new Error('Proxy failed');
                    const blob = await response.blob();
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = f.name || f.url.split('/').pop().split('?')[0];
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } catch (err) {
                    window.open(f.url, '_blank');
                  }
                }}
                sx={{
                  color: "#059669",
                  cursor: "pointer",
                  textDecoration: "none",
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  "&:hover": { textDecoration: "underline" }
                }}
              >
                {f.name}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 700, fontSize: '0.75rem' }}>{f.name}</Typography>
            )}
            <IconButton size="small" onClick={() => handleRemove(idx)} sx={{ p: 0.25, ml: 0.5, color: '#059669', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' } }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Stack>
    );
  });

  // File Upload Field
  if (type === "file") {
    return (
      <Box sx={sx}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#374151', fontSize: '0.825rem' }}>
          {label}
          {required && (
            <Typography component="span" sx={{ color: "#EF4444", ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
        <FileList files={files} onRemove={onFileRemove} />
        <Button
          variant="outlined"
          size="small"
          sx={{
            mt: 1,
            borderColor: 'rgba(117, 34, 209, 0.3)',
            color: '#7522D1',
            backgroundColor: 'rgba(117, 34, 209, 0.02)',
            fontWeight: 700,
            '&:hover': {
              borderColor: '#7522D1',
              backgroundColor: 'rgba(117, 34, 209, 0.06)',
            },
          }}
          onClick={onFileChoose}
          disabled={disabled}
        >
          Choose files
        </Button>
        {(supportedFormats || maxSize) && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            {supportedFormats && (
              <Typography variant="caption" sx={{ color: "#4F4937" }}>
                Files supported: {supportedFormats}
              </Typography>
            )}
            {maxSize && (
              <Typography variant="caption" sx={{ color: "#4F4937" }}>
                Maximum size: {maxSize}
              </Typography>
            )}
          </Box>
        )}
        {helperText && (
          <Typography variant="caption" sx={{ color: error ? "#D32F2F" : "#666", mt: 0.5, display: "block" }}>
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }

  // Checkbox Field
  if (type === "checkbox") {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={value || false}
            onChange={handleChange}
            name={name}
            disabled={disabled}
          />
        }
        label={label}
        sx={sx}
      />
    );
  }

  // Radio Group Field
  if (type === "radio") {
    return (
      <FormControl fullWidth required={required} disabled={disabled} error={error} sx={sx}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {label}
          {required && (
            <Typography component="span" sx={{ color: "#D32F2F", ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
        <RadioGroup
          name={name}
          value={value || ""}
          onChange={handleChange}
        >
          {options.map((option) => (
            <FormControlLabel
              key={typeof option === 'object' ? option.value : option}
              value={typeof option === 'object' ? option.value : option}
              control={<Radio />}
              label={typeof option === 'object' ? option.label : option}
            />
          ))}
        </RadioGroup>
        {helperText && (
          <Typography variant="caption" sx={{ color: error ? "#D32F2F" : "#666", mt: 0.5 }}>
            {helperText}
          </Typography>
        )}
      </FormControl>
    );
  }

  // Autocomplete / Searchable Dropdown
  if (type === "autocomplete") {
    return (
      <Autocomplete
        fullWidth
        disabled={disabled}
        options={options}
        onInputChange={onInputChange}
        filterOptions={(x) => x}
        getOptionLabel={(option) => {
          if (typeof option === 'object') return option.label || "";
          return option || "";
        }}
        value={value ? (options.find(opt => (typeof opt === 'object' ? opt.value : opt) === value) || value) : null}
        onChange={(event, newValue) => {
          const actualValue = typeof newValue === 'object' ? (newValue?.value || "") : (newValue || "");
          onChange(name, actualValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={error && errorText ? errorText : helperText}
            placeholder={placeholder}
          />
        )}
        sx={sx}
      />
    );
  }

  // Select/Dropdown field
  if (type === "select") {
    return (
      <FormControl fullWidth required={required} disabled={disabled} error={error} sx={sx}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          name={name}
          value={value || ""}
          onChange={handleChange}
        >
          {options.map((option) => (
            <MenuItem
              key={typeof option === 'object' ? option.value : option}
              value={typeof option === 'object' ? option.value : option}
            >
              {typeof option === 'object' ? option.label : option}
            </MenuItem>
          ))}
        </Select>
        {(helperText || errorText) && (
          <Typography variant="caption" sx={{ color: error ? "#D32F2F" : "#666", mt: 0.5, ml: 1.5 }}>
            {error && errorText ? errorText : helperText}
          </Typography>
        )}
      </FormControl>
    );
  }

  // Date field
  if (type === "date") {
    // Convert ISO date string to yyyy-MM-dd format for HTML date input
    const formatDateForInput = (dateValue) => {
      if (!dateValue) return "";
      
      // If already in yyyy-MM-dd format, return as-is
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // Handle ISO date strings and other formats
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "";
        
        // Get local date components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.warn('Invalid date format:', dateValue);
        return "";
      }
    };

    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        type="date"
        value={formatDateForInput(value)}
        onChange={handleChange}
        disabled={disabled}
        error={error}
        helperText={error && errorText ? errorText : helperText}
        InputLabelProps={{ shrink: true }}
        inputProps={inputProps}
        sx={sx}
      />
    );
  }

  // Textarea / Multiline field
  if (type === "textarea" || multiline) {
    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={error && errorText ? errorText : helperText}
        multiline
        rows={rows}
        minRows={minRows || 4}
        inputProps={inputProps}
        sx={sx}
      />
    );
  }

  // Number field
  if (type === "number") {
    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        type="number"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={error && errorText ? errorText : helperText}
        inputProps={inputProps}
        sx={sx}
      />
    );
  }

  // Email field
  if (type === "email") {
    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        type="email"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={error && errorText ? errorText : helperText}
        inputProps={inputProps}
        sx={sx}
      />
    );
  }

  // Phone/Tel field
  if (type === "tel" || type === "phone") {
    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        type="tel"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={error && errorText ? errorText : helperText}
        inputProps={inputProps}
        sx={sx}
      />
    );
  }

  // Default: Text field
  return (
    <TextField
      fullWidth
      required={required}
      label={label}
      name={name}
      type="text"
      value={value || ""}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      helperText={error && errorText ? errorText : helperText}
      inputProps={inputProps}
      sx={sx}
    />
  );
};


